import * as React from "react";
import {cx} from "emotion";

import "./pricing-table-3.scss";

import {cs}                       from "@Common/react/chain-services";
import {fragments}                from "@Common/react/fragments";
import {keyed, keyed1}            from "@Common/react/keyed";
import {UseState}                 from "@Common/react/use-state";
import {DataTableD}               from "@Common/ui-components/heavy/common/design2/data-table/desktop/data-table-d";
import {Select}                   from "@Common/ui-components/heavy/common/design2/select/select";
import {formatMoney1}             from "@Common/ui-components/language/format-money";
import {formatNumberBig}          from "@Common/ui-components/language/format-number-big";
import {sort}                     from "@Common/utils/collections";
import {chain}                    from "@Common/utils/fs";
import {entries}                  from "@Common/utils/objects";
import {conflict, fragmentRanges} from "@Common/utils/ranges";
import {to_getFriendlyName} from "@Common/logic/template-options/template-options-get-friendly-name";

/**
 * This pricing table show multiple template options
 * All delivery methods are merged into 1 table
 */
export const PricingTable3 = ({list}) => cs(

    ["tabIndex", (_, next) => UseState({initValue: 0, next})],
    ({tabIndex}, next) => keyed1({key: tabIndex.value, next}),
    ["templateOptions", ({tabIndex}, next) => next(list[tabIndex.value])],
    ["paperType", (_, next) => UseState({next})],
    ["paperTypes", ({templateOptions}, next) => next(
        templateOptions.options.filter((o) => (
            o.group === "PaperTypes" &&
            valueColumns.some((col) => col.getPrices(o))
        ))
    )],
    ["calPaperType", ({paperTypes, paperType, templateOptions}, next) => next((() => {
        return paperType.value && paperTypes.includes(paperType.value) ? paperType.value : paperTypes[0];
    })())],
    ({tabIndex, templateOptions, paperType, paperTypes, calPaperType}) => (
        <div className="pricing-table-3-63u">
            {cs(
                ({}, next) => list.length > 1 && next(),
                () => (
                    <div className="tabs">
                        {list.map((to, i) => cs(
                            keyed(JSON.stringify(to.sku)),
                            () => (
                                <div {...{
                                    className: cx("item", {active: to === templateOptions}),
                                    onClick: () => tabIndex.onChange(i),

                                    "xd-qa-type": "button",
                                    "xd-qa-name": "Template Options Tab #"+(i+1),
                                }}>
                                    {to_getFriendlyName(to) ?? to?.product?.name}
                                </div>
                            )
                        ))}
                    </div>
                ),
            )}

            <div className="content">

                {list.length === 1 && (
                    <div className="template-name">
                        {to_getFriendlyName(templateOptions) ?? templateOptions?.product?.name}
                    </div>
                )}

                {cs(
                    ({}, next) => paperTypes.length !== 1 && next(),
                    ({}) => (
                        <div className="paper-type">
                            <div className="label">
                                Paper Type
                            </div>
                            {Select({
                                list: paperTypes,
                                itemToLabel: (pt) => pt.name,
                                isSelected: (pt) => pt === calPaperType,
                                onChange: (pt) => paperType.onChange(pt),
                                xdQaName: "Paper Type",
                            })}
                        </div>
                    ),
                )}

                {cs(
                    ["qtyRanges", (_, next) => next(fragmentRanges(
                        valueColumns.map((col) => getPriceRanges(col.getPrices(calPaperType) ?? {})).flat()
                    ))],
                    ["expanded", (_, next) => UseState({next})],
                    ({expanded, qtyRanges}, next) => fragments(
                        next(),
                        qtyRanges.length > 4 && (
                            <div className="expand-indicator">
                                <a {...{
                                    onClick: () => expanded.change(v=>!v),
                                }}>
                                    {expanded.value ? (
                                        "- View Less Tiers"
                                    ) : (
                                        "+ View More Tiers"
                                    )}
                                </a>
                            </div>
                        )
                    ),
                    ({qtyRanges, expanded}) => DataTableD2({
                        list: chain(
                            qtyRanges,
                            (_) => expanded.value ? _ : _.slice(0, 4),
                        ),
                        columns: [
                            {
                                label: "QUANTITY",
                                render: (quantityRange) => (
                                    quantityRange.from == null && quantityRange.to == null ? (
                                        "all"
                                    ) : quantityRange.from == null ? (
                                        formatNumberBig(quantityRange.to - 1) + " and below"
                                    ) : quantityRange.to == null ? (
                                        formatNumberBig(quantityRange.from) + " and above"
                                    ) : quantityRange.from === quantityRange.to - 1 ? (
                                        formatNumberBig(quantityRange.from)
                                    ) : (
                                        formatNumberBig(quantityRange.from) + "-" + formatNumberBig(quantityRange.to - 1)
                                    )
                                ),
                            },
                            ...valueColumns.map((col) => {
                                const prices = normalizePrices(col.getPrices(calPaperType) ?? {});
                                return ({
                                    label: col.label.toUpperCase(),
                                    render: (quantityRange) => {
                                        const record = prices.find((r) => conflict(r.quantityRange, quantityRange));
                                        return !record ? "--" : formatMoney1(prices.find((r) => conflict(r.quantityRange, quantityRange)).price);
                                    },
                                });
                            }),
                        ],
                    }),
                )}
            </div>
        </div>
    ),
);

const DataTableD2 = ({list, columns}) => DataTableD({
    rows: list.map((record) => ({
        columns: columns.map((col) => ({
            render: () => col.render(record),
        })),
    })),
    columns: columns.map((col) => ({
        width: "1%",
        label: col.label,
    })),
});

const valueColumns = [
    {
        label: "First Class Mail",
        getPrices: (pt) => pt?.productPrice?.mailing?.["1"],
    },
    {
        label: "Standard Mail",
        getPrices: (pt) => pt?.productPrice?.mailing?.["3"],
    },
    {
        label: "Shipping",
        getPrices: (pt) => pt?.productPrice?.shipping,
    },
];

const normalizePrices = (prices) => chain(
    prices,
    (_) => entries(_),
    (_) => _.map(([key, qty]) => ({
        price: +(qty.price ?? key),
        quantityRange: {
            from: qty.min,
            to  : qty.max==null ? null : qty.max+1,
        },
    })),
    (_) => sort(_, ({quantityRange}) => quantityRange.from ?? -1),
);

const getPriceRanges = (prices) => {
    const list = normalizePrices(prices);

    return list.map(({quantityRange}) => quantityRange);
};

// const prices = {
//     "0.60" : {"min": 20000},
//     "0.55": {"min": 10000, max: 20000},
//     "0.65": {"min": 5000, "max":9999},
//     // "0.66": {"min": 5000, "max":9999},
//     // "0.67": {"min": 5000, "max":9999},
//     "0.70": {"min": 1000, "max":4999},
//     "0.75": {"min": 500, "max":999},
//     "1.10": {"min": 200, "max":499},
//     "0.85": {"min": 25, "max":99}
// };
